import type { QueryClient } from "@tanstack/react-query";
import { qk } from "@/lib/query/queryKeys";

/**
 * Mark everything a guest write invalidates.
 *
 * This was `GuestsProvider.invalidateContingent`, and living inside the provider
 * was the defect: it is the ONLY thing that marks the guest-list caches stale
 * after a check-in, and `GuestsProvider` is mounted on exactly three surfaces
 * (the guest list, the requests page, `StandaloneGuestDetail`). The check-in
 * history page is not one of them — both write doors there call
 * `runPartyCheckIn` directly, correctly, and used to invalidate nothing. Neither
 * does the `BATCH_CHECK_IN_GUESTS` job, which completes from the app root.
 *
 * The three `checkInActionCompleted` listeners are not a substitute and never
 * were: `CheckInLogsProvider` refetches the log rows, `MetaTableProvider` bumps
 * the check-in table's count (and skips `type === "guest"` outright), and
 * `PGuestDetails` re-reads one open pane. None touches `["guestlist"]`.
 *
 * With a 30 s app-wide `staleTime` — and a guest list that paints from
 * prefetched rows on first render rather than fetching — an operator who checks
 * people in on the history page and walks to the list sees them as not checked
 * in.
 *
 * Takes the client rather than reading the module singleton, so a component can
 * pass the one from its own `useQueryClient()`. The key list is unchanged from
 * the provider's original; this is a move, not a redesign.
 */
export function invalidateAfterCheckIn(
  queryClient: QueryClient,
  eventId: string,
): void {
  queryClient.invalidateQueries({ queryKey: ["guestlist", eventId, "company"] });
  queryClient.invalidateQueries({ queryKey: ["eventLayout", eventId, "event"] });
  queryClient.invalidateQueries({ queryKey: qk.events.all });
  queryClient.invalidateQueries({ queryKey: qk.events.detail(eventId) });
  // The whole guestlist namespace (rows/count/init) + the standalone
  // guest-detail popup seeds are cached snapshots that outlive the provider.
  // Without these, a sub-staleTime remount re-seeds from the pre-write snapshot
  // (created guest vanishes, deleted resurrects, popup reopens on pre-edit
  // values). refetchType "none": a mounted table is kept live by its own
  // refresh, and a burst of refetches here would fight it.
  queryClient.invalidateQueries({ queryKey: ["guestlist", eventId], refetchType: "none" });
  queryClient.invalidateQueries({ queryKey: ["guestDetailGuest", eventId], refetchType: "none" });
  queryClient.invalidateQueries({ queryKey: ["guestDetailSeed", eventId], refetchType: "none" });
  // The requests-page snapshots are the same kind of outliving cache — a popup
  // edit made on that page (StandaloneGuestDetail mounts its own
  // GuestsProvider) must not resurrect pre-edit rows on a sub-staleTime remount.
  queryClient.invalidateQueries({ queryKey: ["guestsRequests", eventId], refetchType: "none" });
}
